import { BadgeStatus } from '@/components/badge-order';
import DynamicImage from '@/components/commons/dynamic-image';
import LoadingCustomerOrderDetail from '@/components/sections/builder/elements/body/customer/customer_order/component/loading-detail';
import OrderActionButton from '@/components/sections/builder/elements/body/customer/customer_order/component/order/order-action-button';
import { CustomerOrderElementDataType } from '@/components/sections/builder/elements/body/customer/customer_order/type';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useExternalOrderDetail } from '@/hooks/api/storefront/orders';
import useModal from '@/hooks/useModal';
import { cn, formatIDR } from '@/lib/utils';
import { StoreCustomerOrderDetailItem } from '@/types/http';
import { XIcon } from 'lucide-react';
import React from 'react';
import DeliverySection from '@/components/sections/builder/elements/body/customer/customer_order/component/order/delivery-section';
import PickupSection from '@/components/sections/builder/elements/body/customer/customer_order/component/order/pickup-section';

type DetailDrawerProps = {
  content: CustomerOrderElementDataType;
  openModal?: boolean;
  setOpenModal?: (open: boolean) => void;
  data: { order_id: string };
  token?: string;
  modalNotify?: ReturnType<typeof useModal>;
};

export function DetailDrawer({
  content,
  openModal,
  setOpenModal,
  data,
  token,
  modalNotify,
}: DetailDrawerProps) {
  const orderId = data?.order_id || null;

  const { data: dataOrder, isLoading } = useExternalOrderDetail(
    orderId as string,
    token as string,
    {
      enabled: Boolean(openModal && orderId),
    }
  );

  const handleInvoice = () => {
    window.open(`/account/order/invoice/${dataOrder?.order_id}`, '_blank');
  };

  const handleInvoiceDropship = () => {
    window.open(`/account/order/invoice-dropshipper/${dataOrder?.order_id}`, '_blank');
  };

  const handlePayment = () => {
    if (!dataOrder?.payment_link) return;
    window.open(dataOrder?.payment_link as string);
  };

  const handleTrackOrder = () => {
    if (!dataOrder?.awb_track_link) return;
    window.open(dataOrder?.awb_track_link as string);
  };

  return (
    <Drawer open={openModal} onOpenChange={setOpenModal} direction='right'>
      <DrawerContent className='fixed inset-y-0 right-0 z-[106] flex h-auto w-full flex-col bg-white md:max-w-[450px] xl:max-w-[650px]'>
        <DrawerHeader className='flex items-center justify-between text-left'>
          <DrawerTitle className='text-xl xl:text-2xl'>Order Detail</DrawerTitle>
          <DrawerClose>
            <XIcon className='h-4 w-4' />
          </DrawerClose>
        </DrawerHeader>
        {isLoading ? (
          <LoadingCustomerOrderDetail />
        ) : (
          <>
            <ScrollArea className='h-full w-full px-5 pb-6'>
              <div className='mb-8 flex w-full flex-col gap-3 xl:mb-12'>
                <span className='text-lg xl:text-xl'>#{dataOrder?.order_no || '-'}</span>
                <div className='w-full'>
                  <BadgeStatus status={dataOrder?.status.toLowerCase() || 'unknown'} />
                </div>
              </div>

              <OrderActionButton
                status={dataOrder?.status.toLowerCase() || 'unknown'}
                content={content}
                handlePayment={handlePayment}
                handleTrackOrder={handleTrackOrder}
                handleInvoice={handleInvoice}
                shippingType={dataOrder?.shipment_type || 'shipping'}
                orderId={dataOrder?.order_id || ''}
                setOpenModal={setOpenModal}
                modalNotify={modalNotify}
                handleInvoiceDropship={handleInvoiceDropship}
                is_dropship={dataOrder?.is_dropship || false}
              />

              <Separator className='my-4 border-gray-200 xl:my-6' />

              {dataOrder?.shipment_type === 'pickup_in_store' ? (
                <PickupSection dataOrder={dataOrder} />
              ) : (
                <DeliverySection dataOrder={dataOrder} />
              )}

              <div className='mb-3 border-b border-gray-200 pb-3 xl:mb-6 xl:pb-6'>
                <h2 className='mb-3 text-lg font-bold xl:mb-6 xl:text-xl'>
                  Item ({(dataOrder?.order_items && dataOrder?.order_items.length) || 0})
                </h2>
                <div className='flex w-full flex-col gap-y-4 md:gap-y-6 xl:gap-y-8'>
                  {dataOrder?.order_items &&
                    dataOrder?.order_items.length > 0 &&
                    dataOrder?.order_items?.map((item: StoreCustomerOrderDetailItem) => {
                      const isDiscount = (item?.discount_price ?? 0) > 0;
                      const hasPromoDiscount = Number(item?.disc_amount) > 0;

                      const promoPrice =
                        Number(item?.price) * item?.qty - Number(item?.disc_amount);

                      return (
                        <div className='grid grid-cols-2 gap-x-4 text-gray-700' key={item.item_id}>
                          <div className='flex items-center gap-4 text-sm xl:text-base'>
                            <div className='relative h-12 w-12 flex-shrink-0 bg-gray-50 xl:h-16 xl:w-16'>
                              <DynamicImage
                                src={item.image as string}
                                alt={item.item_name as string}
                                width={64}
                                height={64}
                                className='aspect-square rounded-md object-contain md:rounded-lg'
                                sizes='100vw'
                              />
                            </div>
                            <div className='flex flex-col overflow-hidden'>
                              <h3 className='truncate font-bold '>{item?.product_name || '-'}</h3>
                              {item.product_name !== item.item_name && (
                                <span className='truncate'> {item.item_name}</span>
                              )}
                              <span className='text-sm'>x{item.qty}</span>
                            </div>
                          </div>
                          <div className='flex flex-col justify-between text-right text-sm xl:text-base'>
                            {hasPromoDiscount ? (
                              <span className='justify-center whitespace-nowrap font-bold'>
                                {promoPrice === 0 ? 'Free' : formatIDR(promoPrice)}
                              </span>
                            ) : (
                              isDiscount && (
                                <span className='justify-center whitespace-nowrap font-bold'>
                                  {formatIDR(Number(item.disc_amount))}
                                </span>
                              )
                            )}
                            <span
                              className={cn(
                                'justify-center whitespace-nowrap',
                                item?.discount_price || item?.disc_amount
                                  ? 'text-gray-400 line-through'
                                  : 'font-bold'
                              )}
                            >
                              {formatIDR(item?.price * item.qty)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              <div className='mb-3 xl:mb-6 xl:pb-6'>
                <span className='text-sm font-semibold'>Notes:</span>
                <p className='text-base'>{dataOrder?.notes || '-'}</p>
              </div>

              {/* Order Summary */}
              <div className='mb-3 border-b border-gray-200 pb-3 xl:mb-6 xl:pb-6'>
                <h2 className='mb-5 text-lg font-bold xl:text-xl'>Order Summary</h2>
                <div className='space-y-2 text-sm xl:text-base'>
                  <div className='flex justify-between'>
                    <span>Subtotal</span>
                    <span>{formatIDR(dataOrder?.subtotal ?? 0)}</span>
                  </div>
                  {(dataOrder?.total_disc as number) > 0 && (
                    <div className='flex justify-between'>
                      <span>Product discounts</span>
                      <span className='text-red-500'>
                        {formatIDR(dataOrder?.total_disc as number)}
                      </span>
                    </div>
                  )}
                  {(dataOrder?.disc_transaction as number) > 0 && (
                    <div className='flex justify-between'>
                      <span>Transaction discounts</span>
                      <span className='text-red-500'>
                        {formatIDR(dataOrder?.disc_transaction as number)}
                      </span>
                    </div>
                  )}
                  {dataOrder?.shipment_type !== 'pickup_in_store' && (
                    <>
                      <div className='flex justify-between'>
                        <span>Shipping cost</span>
                        <span>{formatIDR(Number(dataOrder?.shipping_cost || 0))}</span>
                      </div>
                      {dataOrder?.is_include_insurance && (
                        <div className='flex justify-between'>
                          <span>Shipping insurance</span>
                          <span>{formatIDR(dataOrder?.insurance_cost ?? 0)}</span>
                        </div>
                      )}
                      {dataOrder?.is_cod && (
                        <div className='flex justify-between'>
                          <span>COD fee</span>
                          <span>{formatIDR(dataOrder?.cod_fee || 0)}</span>
                        </div>
                      )}
                      {Number(dataOrder?.shipping?.shipper_promotion_value) > 0 && (
                        <div className='flex justify-between'>
                          <span>Shipping discount</span>
                          <span>
                            {formatIDR(Number(dataOrder?.shipping?.shipper_promotion_value || 0))}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  {dataOrder?.charge_fee_to === 'buyer' && (
                    <div className='flex justify-between'>
                      <span>Platform fee</span>
                      <span>{formatIDR(dataOrder?.platform_fee ?? 0)}</span>
                    </div>
                  )}
                  <div className='flex justify-between'>
                    <span>Tax</span>
                    <span>{formatIDR(dataOrder?.total_tax ?? 0)}</span>
                  </div>
                </div>
              </div>
              <div className='flex items-center justify-between text-lg font-bold xl:text-xl'>
                <span>Total</span>
                <span>{formatIDR(dataOrder?.grand_total ?? 0)}</span>
              </div>
            </ScrollArea>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
